/**
 * DPanel API fetch wrapper.
 *
 * - Base URL `/api/v1` (Vite dev proxy → http://localhost:8000; in prod
 *   nginx serve sia frontend che API sotto lo stesso host).
 * - `credentials: "include"` per il cookie session HMAC-signed.
 * - 401 → throw {@link AuthRequiredError}: il router root la cattura
 *   e fa redirect a `/login` preservando la URL originale.
 * - 4xx/5xx → throw {@link ApiError} con il body RFC 7807 problem+json
 *   parsed (backend Phase 5 §02-error-catalog: 64 kind canonical).
 */

import { t as i18nT } from "i18next";
import { ZodError } from "zod";

const BASE_URL = "/api/v1";

/** Cookie name del CSRF double-submit token (vedi backend middleware/csrf.py). */
const CSRF_COOKIE_NAME = "__Host-dpanel_csrf";

/** Metodi HTTP che richiedono il CSRF token (devono matchare backend UNSAFE_METHODS). */
const CSRF_UNSAFE_METHODS = new Set(["POST", "PATCH", "PUT", "DELETE"]);

/**
 * Legge il valore del cookie CSRF dal document.cookie.
 *
 * Il backend emette `__Host-dpanel_csrf` su login (NOT HttpOnly), con
 * Path=/ e SameSite=Strict. La SPA lo echo nell'header X-CSRF-Token sulle
 * mutation (pattern double-submit, ASVS V4.2.2).
 *
 * Ritorna `null` se il cookie non esiste (es. utente non loggato, o backend
 * non ha ancora rilasciato il cookie — il caller fa fallire la mutation con
 * 403 csrf_token_missing che e' la response corretta).
 */
export function readCsrfCookie(): string | null {
  if (typeof document === "undefined") return null; // SSR/node-test guard
  const prefix = `${CSRF_COOKIE_NAME}=`;
  const parts = document.cookie.split(";");
  for (const raw of parts) {
    const c = raw.trim();
    if (c.startsWith(prefix)) {
      return decodeURIComponent(c.slice(prefix.length));
    }
  }
  return null;
}

export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  /** Backend `kind` slug — canonical error type (es. "invalid_credentials"). */
  kind?: string;
  /** Request ID dal header X-Request-ID (utile per supporto). */
  request_id?: string;
  /** Estensione: campi specifici del kind (es. retry_after_seconds). */
  [extension: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails;
  readonly requestId: string | undefined;

  constructor(problem: ProblemDetails, requestId?: string) {
    super(problem.detail ?? problem.title);
    this.name = "ApiError";
    this.status = problem.status;
    this.problem = problem;
    this.requestId = requestId;
  }
}

/** 401 / 403 / session-expired: il caller deve redirect a /login. */
export class AuthRequiredError extends ApiError {
  constructor(problem: ProblemDetails, requestId?: string) {
    super(problem, requestId);
    this.name = "AuthRequiredError";
  }
}

/**
 * Singolo entry in `errors[]` di un problem+json validation_failed (Phase 5
 * §02-error-catalog). `loc` ha tipicamente prefisso "body." o "query."; `msg`
 * e' il messaggio Pydantic; `type` e' il tipo error code Pydantic v2.
 */
export interface ApiFieldError {
  loc: string;
  msg: string;
  type?: string;
}

/**
 * Mapping problem.kind → i18n key per messaggi user-friendly nella UI.
 *
 * Quando il BE risponde con un kind elencato qui, la UI mostra il testo
 * tradotto invece del `detail` tecnico (spesso path interni o riferimenti
 * a tabelle/role token comprensibili solo agli sviluppatori).
 *
 * Phase 10F: copertura iniziale per kind che esercita la UI gestione
 * utenti. Phase 10H+ estende ai 64 kind del catalogo (errors/02-error-catalog.md).
 */
const KIND_TO_I18N: Record<string, string> = {
  mfa_required: "errors.mfa_required_hint",
  read_only_role: "errors.read_only_role",
  approval_invalid_state: "errors.approval_invalid_state",
  approval_self_decision_forbidden: "errors.approval_self_decision_forbidden",
  csrf_token_missing: "errors.csrf_token_missing",
  idempotency_key_required: "errors.idempotency_key_required",
  step_up_required: "errors.step_up_required",
  rate_limited: "errors.rate_limited",
  internal_error: "errors.internal_error",
  storage_unavailable: "errors.storage_unavailable",
  service_unavailable: "errors.service_unavailable",
  resource_state_invalid: "errors.resource_state_invalid",
  invalid_credentials: "errors.invalid_credentials",
};

/**
 * Format an ApiError per visualizzazione UI:
 * - Se `problem.kind` ha un formatter in KIND_FORMATTERS: messaggio tradotto
 *   composto dagli extension field (quota_cap_exceeded, ...).
 * - Se `problem.kind` e' mappato in KIND_TO_I18N: ritorna il messaggio
 *   tradotto (mfa_required, approval_invalid_state, ...).
 * - Se la response 422 ha `errors[]` field-specific: ritorna un array di
 *   stringhe "campo: messaggio" (con loc strip-pato di "body.").
 * - Altrimenti: ritorna `[problem.detail ?? problem.title]` (fallback BE).
 *
 * Usage in form:
 *   const lines = formatApiError(mutation.error);
 *   {lines.map(l => <li key={l}>{l}</li>)}
 */
export function formatApiError(error: unknown): string[] {
  // Client-side Zod validation (the typed hooks `.parse()` the input before the
  // request): surface one readable `field: message` line per issue instead of
  // dumping the raw ZodError JSON. e.g. an empty Audience → "audience: ...".
  if (error instanceof ZodError) {
    return error.issues.map((iss) => {
      const path = iss.path.filter((p) => p !== "").join(".");
      return path ? `${path}: ${iss.message}` : iss.message;
    });
  }
  if (!(error instanceof ApiError)) {
    return [error instanceof Error ? error.message : String(error)];
  }
  const lines: string[] = [];
  const kind = error.problem.kind;
  const formatted = kind ? KIND_FORMATTERS[kind]?.(error.problem as Record<string, unknown>) : undefined;
  if (formatted) {
    lines.push(formatted);
  } else if (kind && KIND_TO_I18N[kind]) {
    lines.push(_t(KIND_TO_I18N[kind]));
  } else {
    const fieldErrors = error.problem["errors"] as ApiFieldError[] | undefined;
    if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
      for (const fe of fieldErrors) {
        const cleanLoc = fe.loc.replace(/^(body|query|path|header)\./, "");
        lines.push(cleanLoc ? `${cleanLoc}: ${fe.msg}` : fe.msg);
      }
    } else {
      lines.push(error.problem.detail ?? error.problem.title);
    }
  }
  // Append the X-Request-ID so support can grep the structlog stream. The
  // server returns it via response header (captured in apiRequest) and ALSO
  // as `problem.request_id` extension (Phase 5 §02-error-catalog).
  const requestId =
    error.requestId ??
    (typeof error.problem.request_id === "string" ? error.problem.request_id : undefined);
  if (requestId) {
    lines.push(`${_t("errors.request_id_label")}: ${requestId}`);
  }
  return lines;
}

/**
 * Wrapper i18next.t() per uso fuori dal React tree.
 *
 * Import top-level di `t` da "i18next": il singleton e' inizializzato in
 * main.tsx (via "@/lib/i18n") PRIMA di qualunque chiamata API, e api.ts e'
 * un leaf module (i18n.ts non importa api.ts → no circular).
 *
 * Se la key non e' caricata, i18next ritorna la key stessa: ritorniamo la
 * key cosi' com'e' (caller decide se mostrarla o nascondere).
 */
function _t(key: string, params?: Record<string, unknown>): string {
  const result = params === undefined ? i18nT(key) : i18nT(key, params);
  return typeof result === "string" ? result : key;
}

/** Label tradotta di una dimensione di quota; fallback al token BE se ignota. */
function _quotaDimLabel(dim: string): string {
  const key = `accounts.quota.dim.${dim}`;
  const label = _t(key);
  return label === key ? dim : label;
}

/**
 * Formatter per kind che portano extension field strutturati: il messaggio
 * tradotto si compone dai campi, non da una stringa fissa. Consultati PRIMA
 * di KIND_TO_I18N; se un formatter ritorna undefined (campi assenti o
 * inattesi) si cade sul `detail` del BE, mai su un messaggio mutilato.
 *
 * Le label di tier sono costruite via template literal, quindi invisibili al
 * checker i18n: dichiarate qui.
 * // i18n-static: errors.quota_tier.account,errors.quota_tier.department,errors.quota_tier.reseller
 */
const KIND_FORMATTERS: Record<string, (problem: Record<string, unknown>) => string | undefined> = {
  // tier: account | department | reseller; dimensions: ["mailbox_count", ...]
  quota_cap_exceeded: (problem) => {
    const raw = problem["dimensions"];
    const dims = Array.isArray(raw) ? raw.filter((d): d is string => typeof d === "string") : [];
    if (dims.length === 0) return undefined;
    const tierToken = typeof problem["tier"] === "string" ? problem["tier"] : "";
    const tierKey = `errors.quota_tier.${tierToken}`;
    const tier = _t(tierKey) === tierKey ? tierToken : _t(tierKey);
    if (!tier) return undefined;
    return _t("errors.quota_cap_exceeded", {
      tier,
      dimensions: dims.map(_quotaDimLabel).join(", "),
    });
  },
  // dimension singola + allocated/pool numerici (allocated null = illimitato)
  reseller_pool_exceeded: (problem) => {
    const dim = problem["dimension"];
    const pool = problem["pool"];
    if (typeof dim !== "string" || typeof pool !== "number") return undefined;
    const allocated = problem["allocated"];
    return _t("errors.reseller_pool_exceeded", {
      dimension: _quotaDimLabel(dim),
      allocated: typeof allocated === "number" ? String(allocated) : _t("errors.quota_unlimited"),
      pool: String(pool),
    });
  },
};

/**
 * Imperative MFA prompt hook (Phase 10F strict MFA gate).
 *
 * Registrato da `<App>` via {@link setMfaPromptHandler} all'inizio del lifecycle.
 * Invocato da {@link apiRequest} quando un endpoint risponde 403 +
 * `kind: "mfa_required"` -> apre il global MfaVerifyDialog.
 *
 * Volutamente disaccoppiato dal React tree per evitare passare il context
 * giu' per ogni mutation/query. Il dialog popup quando serve, l'utente
 * inserisce TOTP, on success deve re-cliccare l'azione (il retry automatico
 * della mutation TanStack richiederebbe API piu' complessa con observer).
 */
/**
 * opts (backlog 8.4): `onVerified` re-esegue l'azione originale dopo un verify
 * MFA riuscito; `onCancelled` viene invocato se l'utente chiude il dialog senza
 * verificare → permette al chiamante di rigettare la Promise in sospeso invece
 * di lasciarla appesa. Struttura inline (non importa MfaPromptOptions) per
 * evitare un import circolare con mfa-verify-prompt.ts.
 */
type MfaPromptHandler = (opts?: { onVerified?: () => void; onCancelled?: () => void }) => void;

let _mfaPromptHandler: MfaPromptHandler | null = null;

export function setMfaPromptHandler(handler: MfaPromptHandler | null): void {
  _mfaPromptHandler = handler;
}

function _maybePromptMfa(problem: ProblemDetails, status: number): void {
  if (status === 403 && problem.kind === "mfa_required" && _mfaPromptHandler) {
    // Fire-and-forget: il dialog gestisce TOTP verify + chiusura. Usato dai
    // path che non possono ri-eseguirsi automaticamente (es. multipart upload,
    // la cui FormData è già consumata) → l'utente ri-tenta a mano.
    _mfaPromptHandler();
  }
}

/**
 * backlog 8.4: auto-retry trasparente dopo MFA. Su 403 `mfa_required`, apre il
 * dialog globale e — a verify riuscito — RI-ESEGUE l'azione originale, risolvendo
 * la Promise col risultato del retry (la mutation TanStack resta `pending` finché
 * il dialog è aperto, poi va a `success` senza che l'utente ri-clicchi). Su
 * annullamento, rigetta con l'errore `mfa_required` originale (il form mostra
 * l'errore come prima). Nessun handler registrato → rigetta subito.
 */
function _retryAfterMfa<T>(
  retry: () => Promise<T>,
  problem: ProblemDetails,
  requestId: string | undefined,
): Promise<T> {
  const handler = _mfaPromptHandler;
  if (!handler) {
    return Promise.reject(new ApiError(problem, requestId));
  }
  return new Promise<T>((resolve, reject) => {
    handler({
      onVerified: () => {
        retry().then(resolve, reject);
      },
      onCancelled: () => {
        reject(new ApiError(problem, requestId));
      },
    });
  });
}

/**
 * Phase 11.6: helper pubblico per consumer che usano `fetch()` direct
 * (es. multipart uploads dove `apiRequest` JSON-stringify il File).
 *
 * Comportamento simmetrico a `apiRequest` per le response non-ok:
 * - Parse problem+json (fallback su title/status se non JSON)
 * - 401 → throw `AuthRequiredError`
 * - 403 mfa_required → triggera il MfaVerifyDialog globale via
 *   `_maybePromptMfa`, poi throw `ApiError` (FE mostra hint i18n).
 * - Altri → throw `ApiError`.
 *
 * Use case: `useBrandAssetUpload` (FormData multipart) deve emettere lo
 * stesso comportamento di error-handling di apiRequest senza poter
 * passare per la pipeline JSON.
 */
/**
 * Problem sintetico per una risposta NON JSON.
 *
 * Quando a rispondere e' un intermediario (nginx, un load balancer) il corpo e'
 * HTML e il client non ha un `kind` su cui ragionare. Il fallback storico
 * (`statusText || "Unknown error"`) produceva letteralmente "Unknown error",
 * perche' su HTTP/2 il reason phrase non esiste e `statusText` e' sempre vuoto:
 * un rate limit sul login si presentava all'utente come errore ignoto.
 *
 * Qui lo status viene tradotto nel `kind` corrispondente del catalogo, cosi'
 * `formatApiError` mostra il messaggio localizzato (429 -> rate_limited).
 */
function _problemFromStatus(status: number, statusText: string): ProblemDetails {
  const kind =
    status === 429
      ? "rate_limited"
      : status === 503 || status === 502 || status === 504
        ? "service_unavailable"
        : undefined;
  return {
    type: "about:blank",
    title: statusText || `HTTP ${status}`,
    status,
    ...(kind ? { kind } : {}),
  };
}

export async function throwFromResponse(response: Response): Promise<never> {
  const requestId = response.headers.get("X-Request-ID") ?? undefined;
  const contentType = response.headers.get("content-type") ?? "";
  const isJson = contentType.includes("json");

  const problem: ProblemDetails = isJson
    ? ((await response.json()) as ProblemDetails)
    : _problemFromStatus(response.status, response.statusText);
  if (response.status === 401) {
    throw new AuthRequiredError(problem, requestId);
  }
  _maybePromptMfa(problem, response.status);
  throw new ApiError(problem, requestId);
}

export interface ApiRequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** Header Idempotency-Key per POST critici / DELETE distruttivi. */
  idempotencyKey?: string;
  /** X-Step-Up-Token quando un'action approval-gated lo richiede. */
  stepUpToken?: string;
  /** Aborta la richiesta esternamente (utile con React Query). */
  signal?: AbortSignal;
}

/**
 * Esegue una richiesta verso `/api/v1{path}` e ritorna il body JSON
 * tipizzato. Lancia {@link AuthRequiredError} su 401 e {@link ApiError}
 * altrimenti (4xx/5xx).
 */
export async function apiRequest<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const { method = "GET", body, idempotencyKey, stepUpToken, signal } = options;
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (idempotencyKey) {
    headers["Idempotency-Key"] = idempotencyKey;
  }
  if (stepUpToken) {
    headers["X-Step-Up-Token"] = stepUpToken;
  }

  // CSRF double-submit (ASVS V4.2.2): echo cookie value in header per le
  // mutation. Le request GET/HEAD/OPTIONS sono safe per il middleware.
  if (CSRF_UNSAFE_METHODS.has(method)) {
    const csrfToken = readCsrfCookie();
    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }
  }

  const url = path.startsWith("http") ? path : `${BASE_URL}${path}`;
  const response = await fetch(url, {
    method,
    headers,
    credentials: "include",
    body: body !== undefined ? JSON.stringify(body) : null,
    signal,
  });

  const requestId = response.headers.get("X-Request-ID") ?? undefined;

  // 204 No Content (es. /auth/logout, /auth/refresh) → undefined cast a T.
  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") ?? "";
  const isJson = contentType.includes("json");

  if (!response.ok) {
    const problem: ProblemDetails = isJson
      ? ((await response.json()) as ProblemDetails)
      : _problemFromStatus(response.status, response.statusText);
    if (response.status === 401) {
      throw new AuthRequiredError(problem, requestId);
    }
    // backlog 8.4: 403 mfa_required → prompt MFA + auto-retry transparently.
    // The original op was rejected at the MFA gate BEFORE the handler ran (no
    // side effect), so the retry uses a FRESH Idempotency-Key — reusing the old
    // one could replay/conflict against any cached 403.
    if (response.status === 403 && problem.kind === "mfa_required" && _mfaPromptHandler) {
      const retryOptions: ApiRequestOptions = options.idempotencyKey
        ? { ...options, idempotencyKey: crypto.randomUUID() }
        : options;
      return _retryAfterMfa<T>(() => apiRequest<T>(path, retryOptions), problem, requestId);
    }
    throw new ApiError(problem, requestId);
  }

  if (!isJson) {
    return undefined as T;
  }
  return (await response.json()) as T;
}
